import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Moon, LogOut, Users, Clock, CheckCircle2, XCircle, RefreshCw,
  Mail, Phone, Check, X, Trash2,
} from 'lucide-react';

const FILTERS = [
  { key: 'semua', label: 'Semua' },
  { key: 'menunggu', label: 'Menunggu' },
  { key: 'aktif', label: 'Aktif' },
  { key: 'ditolak', label: 'Ditolak' },
];

export default function AdminPanel() {
  const { user, mitra, logout, loadMitra, updateMitraStatus, deleteMitra } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('semua');
  const [loading, setLoading] = useState(true);

  const refresh = async (showToast) => {
    setLoading(true);
    await loadMitra();
    setLoading(false);
    if (showToast) toast.success('Data dimuat ulang.');
  };

  useEffect(() => {
    refresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Kamu telah keluar.');
    navigate('/');
  };

  const stats = useMemo(() => ({
    total: mitra.length,
    menunggu: mitra.filter((m) => m.status === 'menunggu').length,
    aktif: mitra.filter((m) => m.status === 'aktif').length,
    ditolak: mitra.filter((m) => m.status === 'ditolak').length,
  }), [mitra]);

  const filtered = filter === 'semua' ? mitra : mitra.filter((m) => m.status === filter);

  const acc = async (id) => {
    try { await updateMitraStatus(id, 'aktif'); await loadMitra(); toast.success('Mitra di-ACC & sekarang aktif.'); }
    catch { toast.error('Gagal memperbarui.'); }
  };
  const reject = async (id) => {
    try { await updateMitraStatus(id, 'ditolak'); await loadMitra(); toast('Mitra ditolak.'); }
    catch { toast.error('Gagal memperbarui.'); }
  };
  const remove = async (id) => {
    try { await deleteMitra(id); await loadMitra(); toast('Mitra dihapus.'); }
    catch { toast.error('Gagal menghapus.'); }
  };

  return (
    <div className="min-h-screen bg-[#f4eede]">
      <header className="islamic-pattern border-b border-[#d9a94e]/15">
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center">
              <Moon className="w-4 h-4 text-[#0a3b2c]" />
            </div>
            <div>
              <p className="text-white font-medium leading-tight">Pahala Digital</p>
              <p className="text-white/55 text-xs">Panel Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/80 text-sm hidden sm:block">{user?.username}</span>
            <button onClick={handleLogout} className="flex items-center gap-1.5 border border-white/25 text-white text-sm px-3.5 py-2 rounded-lg hover:bg-white/10 transition">
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif-display text-4xl text-[#0a3b2c]">Manajemen Mitra</h1>
            <p className="text-[#4a5a52] text-sm mt-1">Kelola pendaftaran & akses mitra pembeli konten.</p>
          </div>
          <button onClick={() => refresh(true)} className="flex items-center gap-1.5 bg-white border border-[#e8e2d0] text-[#0a3b2c] text-sm px-3.5 py-2 rounded-lg hover:bg-[#f0e9d5] transition whitespace-nowrap">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Muat Ulang
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Mitra" value={stats.total} icon={Users} iconBg="bg-[#0a3b2c]" iconColor="text-white" />
          <StatCard label="Menunggu ACC" value={stats.menunggu} icon={Clock} iconBg="bg-[#fdf0cf]" iconColor="text-[#c9962f]" />
          <StatCard label="Aktif" value={stats.aktif} icon={CheckCircle2} iconBg="bg-[#d9f2e3]" iconColor="text-[#0a7a52]" />
          <StatCard label="Ditolak" value={stats.ditolak} icon={XCircle} iconBg="bg-[#fbe0e0]" iconColor="text-[#c0392b]" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                filter === f.key ? 'bg-[#0a3b2c] text-white' : 'bg-white text-[#4a5a52] border border-[#e8e2d0] hover:bg-[#f0e9d5]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="bg-white border border-[#e8e2d0] rounded-2xl p-10 text-center text-[#4a5a52] text-sm">Belum ada mitra pada kategori ini.</div>
          )}
          {filtered.map((m) => (
            <div key={m.id} className="bg-white border border-[#e8e2d0] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="shrink-0 w-11 h-11 rounded-full bg-[#0a3b2c] text-[#d9a94e] font-semibold flex items-center justify-center">
                  {m.name.charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-[#0a3b2c] font-semibold">{m.name}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[#6b7a72] text-xs mt-0.5">
                    <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{m.email}</span>
                    <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{m.phone}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={m.status} />
                {m.status !== 'aktif' && (
                  <button onClick={() => acc(m.id)} className="flex items-center gap-1 bg-[#0a7a52] text-white text-sm px-3 py-1.5 rounded-lg hover:brightness-110 transition">
                    <Check className="w-4 h-4" /> ACC
                  </button>
                )}
                {m.status !== 'ditolak' && (
                  <button onClick={() => reject(m.id)} className="flex items-center gap-1 border border-[#e2b5b5] text-[#c0392b] text-sm px-3 py-1.5 rounded-lg hover:bg-[#fbe9e9] transition">
                    <X className="w-4 h-4" /> Tolak
                  </button>
                )}
                <button onClick={() => remove(m.id)} className="p-2 text-[#6b7a72] hover:text-[#c0392b] transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, iconBg, iconColor }) {
  return (
    <div className="bg-white border border-[#e8e2d0] rounded-2xl p-5 flex items-center justify-between">
      <div>
        <p className="text-[#6b7a72] text-sm">{label}</p>
        <p className="text-[#0a3b2c] text-3xl font-semibold mt-1">{value}</p>
      </div>
      <span className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    aktif: { label: 'Aktif', cls: 'bg-[#d9f2e3] text-[#0a7a52]' },
    menunggu: { label: 'Menunggu', cls: 'bg-[#fdf0cf] text-[#c9962f]' },
    ditolak: { label: 'Ditolak', cls: 'bg-[#fbe0e0] text-[#c0392b]' },
  };
  const s = map[status] || map.menunggu;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span>;
}
